import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { getAccessToken } from "../../hooks/AccessToken";
import Constant from "../Constant";
import "./UsersPage.css";

const FilterTooltip = ({ text }) => (
  <span className="filter-tooltip-wrapper">
    <span className="filter-tooltip-icon">?</span>
    <span className="filter-tooltip-text">{text}</span>
  </span>
);

function formatRealUserName(user) {
  if (!user) return "-";
  const real = typeof user === "object" ? user.realUserName : user;
  if (real) {
    if (typeof real === "string" && real.trim()) return real.trim();
    if (typeof real === "object") {
      const { firstName = "", middleName = "", lastName = "" } = real;
      const full = [firstName, middleName, lastName].filter(Boolean).join(" ").trim();
      if (full) return full;
    }
  }
  // If realUserName is not explicitly set, use user.name which holds the registration full name
  if (user && user.name && typeof user.name === "string" && user.name.trim()) {
    return user.name.trim();
  }
  return "-";
}

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  const [filterSelections, setFilterSelections] = useState({
    type: "",
    topCount: "",
    timePeriod: "",
    createdWithinDays: ""
  });

  const [appliedFilters, setAppliedFilters] = useState({
    type: "",
    topCount: "",
    timePeriod: "",
    createdWithinDays: ""
  });


  // Custom filter inputs — Account Created
  const [customCreatedNumber, setCustomCreatedNumber] = useState("1");
  const [customCreatedUnit, setCustomCreatedUnit] = useState("weeks");

  // Custom filter inputs — Time Period
  const [customTimePeriodNumber, setCustomTimePeriodNumber] = useState("1");
  const [customTimePeriodUnit, setCustomTimePeriodUnit] = useState("weeks");

  // Popup state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Spotlight assignment state
  const MONTHS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  const currentMonth = MONTHS[new Date().getMonth()];
  const currentYear = new Date().getFullYear();

  const [spotlightModal, setSpotlightModal] = useState(false);
  const [spotlightForm, setSpotlightForm] = useState({
    author: '', month: currentMonth, year: currentYear, imageUrl: '', profileLink: ''
  });
  const [spotlightLoading, setSpotlightLoading] = useState(false);
  const [spotlightResult, setSpotlightResult] = useState(null); // { success, message }

  // Value = number of days; "custom" triggers inline input
  const timePeriodOptions = [
    { label: "All Time", value: "" },
    { label: "1 Week", value: "7" },
    { label: "2 Weeks", value: "14" },
    { label: "1 Month", value: "30" },
    { label: "2 Months", value: "60" },
    { label: "6 Months", value: "180" },
    { label: "Custom...", value: "custom" },
  ];

  const accountCreationOptions = [
    { label: "All Time", value: "" },
    { label: "Last 1 Month", value: "30" },
    { label: "Last 2 Months", value: "60" },
    { label: "Last 6 Months", value: "180" },
    { label: "Custom...", value: "custom" },
  ];

  const toDays = (num, unit) => {
    const n = Math.max(1, parseInt(num) || 1);
    if (unit === "years") return String(n * 365);
    if (unit === "weeks") return String(n * 7);
    return String(n * 30); // months
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const token = getAccessToken();
      try {
        setLoading(true);

        // Always paginated — all filters are additive query params
        let url = `${Constant.apiBasePath}getAll?page=${page}&limit=${itemsPerPage}`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        if (appliedFilters.type) url += `&type=${appliedFilters.type}`;
        if (appliedFilters.topCount) url += `&topCount=${appliedFilters.topCount}`;
        if (appliedFilters.timePeriod) {
          url += `&days=${appliedFilters.timePeriod}`;
        }
        if (appliedFilters.createdWithinDays) {
          url += `&createdWithinDays=${appliedFilters.createdWithinDays}`;
        }

        const response = await axios.post(url, {}, { headers: { token } });

        if (response.data.status === "Success") {
          setUsers(response.data.data);
          setTotalPages(Math.ceil(response.data.totalCount / itemsPerPage));
          setTotalCount(response.data.totalCount);
        } else {
          setError("Failed to fetch users.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [searchQuery, page, appliedFilters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(search);
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilterSelections(prev => {
      const next = { ...prev, [field]: value };
      // Top Count only makes sense when a type is selected — clear it if type is reset
      if (field === "type" && !value) {
        next.topCount = "";
      }
      return next;
    });
  };

  // Applies only the content activity filters (Type, Top Count, Time Period)
  const handleApplyContentFilters = () => {
    let timePeriod = filterSelections.timePeriod;
    if (timePeriod === "custom") {
      timePeriod = toDays(customTimePeriodNumber, customTimePeriodUnit);
    }
    setAppliedFilters(prev => ({ ...prev, type: filterSelections.type, topCount: filterSelections.topCount, timePeriod }));
    setPage(1);
  };

  // Applies only the account registration filter (Account Created)
  const handleApplyAccountFilters = () => {
    let createdWithinDays = filterSelections.createdWithinDays;
    if (createdWithinDays === "custom") {
      createdWithinDays = toDays(customCreatedNumber, customCreatedUnit);
    }
    setAppliedFilters(prev => ({ ...prev, createdWithinDays }));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleNameClick = async (user) => {
    setSelectedUser(user);
    setUserStats(null);
    setStatsLoading(true);
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${Constant.apiBasePath}getUserStats/${user._id}`,
        { headers: { token } }
      );
      if (response.data.status === "Success") {
        setUserStats(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch user stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleClosePopup = () => {
    setSelectedUser(null);
    setUserStats(null);
  };

  const handleOpenSpotlightModal = () => {
    setSpotlightForm({
      author: selectedUser?.name || '',
      month: currentMonth,
      year: currentYear,
      imageUrl: selectedUser?.profilePic || '',
      profileLink: selectedUser?._id
        ? `https://www.storytent.in/userprofile/${selectedUser._id}`
        : '',
    });
    setSpotlightResult(null);
    setSpotlightModal(true);
  };

  const handleCloseSpotlightModal = () => {
    setSpotlightModal(false);
    setSpotlightResult(null);
  };

  const handleSpotlightFormChange = (field, value) => {
    setSpotlightForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSpotlightSubmit = async () => {
    setSpotlightLoading(true);
    setSpotlightResult(null);
    try {
      const token = getAccessToken();
      const payload = {
        author: spotlightForm.author.trim(),
        month: spotlightForm.month,
        year: parseInt(spotlightForm.year),
      };
      if (spotlightForm.imageUrl.trim()) payload.imageUrl = spotlightForm.imageUrl.trim();
      if (spotlightForm.profileLink.trim()) payload.profileLink = spotlightForm.profileLink.trim();

      const response = await axios.post(
        `${Constant.apiBasePath}spotlight/author`,
        payload,
        { headers: { token } }
      );

      if (response.data.status === 'Success') {
        setSpotlightResult({ success: true, message: `${spotlightForm.author} has been assigned as a spotlight author for ${spotlightForm.month} ${spotlightForm.year}.` });
      } else {
        setSpotlightResult({ success: false, message: response.data.message || 'Failed to assign spotlight.' });
      }
    } catch (err) {
      setSpotlightResult({ success: false, message: err.response?.data?.message || err.message || 'Something went wrong.' });
    } finally {
      setSpotlightLoading(false);
    }
  };

  const exportToExcel = async () => {
    const token = getAccessToken();
    try {
      const response = await axios.post(
        "https://api.storytent.in/api/user/get-all-once",
        {},
        { headers: { token } }
      );

      if (response.data.status === "success") {
        const data = response.data.data;
        const formattedData = data.map((user) => ({
          "User Name": user.userName || user.name || "",
          "Real User Name": formatRealUserName(user) !== "-" ? formatRealUserName(user) : (user.name || ""),
          Email: user.email,
          "Created At": new Date(user.createdAt).toLocaleString()
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
        XLSX.writeFile(workbook, "Users.xlsx");
      } else {
        alert("Failed to fetch data for export.");
      }
    } catch (err) {
      alert("Error exporting data: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) return <p className="error-message">Error: {error}</p>;

  return (
    <div className="users-page">
      <h1 className="header">User List</h1>

      {/* Utility Row: Export + Search */}
      <div className="toolbar-utility-row">
        <button onClick={exportToExcel} className="export-button">
          Export to Excel
        </button>
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, email, or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>
      </div>

      {/* Filter Sections */}
      <div className="filter-sections">

        {/* Content Activity */}
        <div className="filter-section">
          <div className="filter-section-title">
            Content Activity
            <FilterTooltip text="Find users based on what they've posted. Type and Time Period narrow the pool; Top Count limits to the highest-posting users." />
          </div>
          <div className="filter-section-body">
            <div className="filter-group">
              <label>
                Type
                <FilterTooltip text="Filter by content type. Required for Top Count to work." />
              </label>
              <select
                value={filterSelections.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                <option value="poem">Poem</option>
                <option value="story">Story</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                Top Count
                <FilterTooltip text="Limit results to the top N users by post count. Requires a Type to be selected." />
              </label>
              <select
                value={filterSelections.topCount}
                onChange={(e) => handleFilterChange("topCount", e.target.value)}
                className="filter-select"
                disabled={!filterSelections.type}
                title={!filterSelections.type ? "Select a Type first" : ""}
              >
                <option value="">All Users</option>
                {[...Array(30)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>
                Time Period
                <FilterTooltip text="Only count posts made within this window. Works with the Type filter." />
              </label>
              {filterSelections.timePeriod !== "custom" ? (
                <select
                  value={filterSelections.timePeriod}
                  onChange={(e) => handleFilterChange("timePeriod", e.target.value)}
                  className="filter-select"
                >
                  {timePeriodOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <div className="custom-date-inputs">
                  <input
                    type="number"
                    min="1"
                    value={customTimePeriodNumber}
                    onChange={(e) => setCustomTimePeriodNumber(e.target.value)}
                    className="custom-number-input"
                  />
                  <select
                    value={customTimePeriodUnit}
                    onChange={(e) => setCustomTimePeriodUnit(e.target.value)}
                    className="filter-select"
                  >
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                  <button
                    type="button"
                    className="custom-date-clear"
                    onClick={() => handleFilterChange("timePeriod", "")}
                    title="Back to presets"
                  >&times;</button>
                </div>
              )}
            </div>

            <button onClick={handleApplyContentFilters} className="apply-filter-button">
              Get
            </button>
          </div>
        </div>

        {/* Account Registration */}
        <div className="filter-section">
          <div className="filter-section-title">
            Account Registration
            <FilterTooltip text="Find users based on when they signed up. Independent of content filters." />
          </div>
          <div className="filter-section-body">
            <div className="filter-group">
              <label>
                Account Created
                <FilterTooltip text="Show only users whose accounts were created within the selected window." />
              </label>
              {filterSelections.createdWithinDays !== "custom" ? (
                <select
                  value={filterSelections.createdWithinDays}
                  onChange={(e) => handleFilterChange("createdWithinDays", e.target.value)}
                  className="filter-select"
                >
                  {accountCreationOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <div className="custom-date-inputs">
                  <input
                    type="number"
                    min="1"
                    value={customCreatedNumber}
                    onChange={(e) => setCustomCreatedNumber(e.target.value)}
                    className="custom-number-input"
                  />
                  <select
                    value={customCreatedUnit}
                    onChange={(e) => setCustomCreatedUnit(e.target.value)}
                    className="filter-select"
                  >
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                  <button
                    type="button"
                    className="custom-date-clear"
                    onClick={() => handleFilterChange("createdWithinDays", "")}
                    title="Back to presets"
                  >&times;</button>
                </div>
              )}
            </div>

            <button onClick={handleApplyAccountFilters} className="apply-filter-button">
              Get
            </button>
          </div>
        </div>

      </div>

      {/* User Table */}
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User Name</th>
              <th>Real User Name</th>
              <th>Email</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr key={user._id}>
                  <td>{(page - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    <span
                      className="clickable-name"
                      onClick={() => handleNameClick(user)}
                    >
                      {user.userName || user.name}
                    </span>
                  </td>
                  <td>{formatRealUserName(user)}</td>
                  <td>{user.email}</td>
                  <td>{new Date(user.createdAt).toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {totalPages} ({totalCount} users)
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}

      {/* User Stats Popup */}
      {selectedUser && (
        <div className="stats-overlay" onClick={handleClosePopup}>
          <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
            <button className="stats-modal-close" onClick={handleClosePopup}>
              &times;
            </button>
            <h2 className="stats-modal-name">{selectedUser.userName || selectedUser.name}</h2>
            <p className="stats-modal-email">{selectedUser.email}</p>

            {statsLoading ? (
              <div className="stats-modal-loading">
                <div className="spinner-small"></div>
                <span>Loading stats...</span>
              </div>
            ) : userStats ? (
              <div className="stats-lists-container">
                <div className="stats-list-column">
                  <h3 className="stats-list-title">Stories ({userStats.storyCount})</h3>
                  <ul className="stats-content-list">
                    {userStats.stories && userStats.stories.length > 0 ? (
                      userStats.stories.map((story) => (
                        <li key={story._id}>
                          <a href={`https://www.storytent.in/story-details/${story._id}`} target="_blank" rel="noopener noreferrer">
                            {story.name}
                          </a>
                        </li>
                      ))
                    ) : (
                      <li className="no-data">No stories found</li>
                    )}
                  </ul>
                </div>
                <div className="stats-list-column">
                  <h3 className="stats-list-title">Poems ({userStats.poemCount})</h3>
                  <ul className="stats-content-list">
                    {userStats.poems && userStats.poems.length > 0 ? (
                      userStats.poems.map((poem) => (
                        <li key={poem._id}>
                          <a href={`https://www.storytent.in/poem-details/${poem._id}`} target="_blank" rel="noopener noreferrer">
                            {poem.name}
                          </a>
                        </li>
                      ))
                    ) : (
                      <li className="no-data">No poems found</li>
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <p className="stats-error">Could not load stats.</p>
            )}

            <button className="assign-spotlight-button" onClick={handleOpenSpotlightModal}>
              ★ Assign Spotlight
            </button>
          </div>
        </div>
      )}

      {/* Spotlight Assignment Modal — rendered after stats popup so it stacks on top */}
      {spotlightModal && (
        <div className="stats-overlay spotlight-overlay" onClick={handleCloseSpotlightModal}>
          <div className="stats-modal spotlight-assign-modal" onClick={(e) => e.stopPropagation()}>
            <button className="stats-modal-close" onClick={handleCloseSpotlightModal}>&times;</button>
            <h2 className="stats-modal-name">Assign Spotlight</h2>
            <p className="stats-modal-email">Create a spotlight entry for this author</p>

            {spotlightResult ? (
              <div className={`spotlight-result ${spotlightResult.success ? 'spotlight-result--success' : 'spotlight-result--error'}`}>
                <p>{spotlightResult.message}</p>
                {spotlightResult.success ? (
                  <button className="spotlight-done-button" onClick={handleCloseSpotlightModal}>Done</button>
                ) : (
                  <button className="spotlight-retry-button" onClick={() => setSpotlightResult(null)}>Try Again</button>
                )}
              </div>
            ) : (
              <>
                <div className="spotlight-form">
                  <div className="spotlight-field">
                    <label>Author Name</label>
                    <input
                      type="text"
                      value={spotlightForm.author}
                      onChange={(e) => handleSpotlightFormChange('author', e.target.value)}
                      className="spotlight-input"
                      placeholder="Author display name"
                    />
                  </div>
                  <div className="spotlight-field-row">
                    <div className="spotlight-field">
                      <label>Month</label>
                      <select
                        value={spotlightForm.month}
                        onChange={(e) => handleSpotlightFormChange('month', e.target.value)}
                        className="spotlight-select"
                      >
                        {MONTHS.map(m => (
                          <option key={m} value={m}>
                            {m.charAt(0).toUpperCase() + m.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="spotlight-field">
                      <label>Year</label>
                      <input
                        type="number"
                        value={spotlightForm.year}
                        onChange={(e) => handleSpotlightFormChange('year', e.target.value)}
                        className="spotlight-input spotlight-input--year"
                        min="2020"
                        max="2099"
                      />
                    </div>
                  </div>
                  <div className="spotlight-field">
                    <label>Image URL</label>
                    <input
                      type="url"
                      value={spotlightForm.imageUrl}
                      onChange={(e) => handleSpotlightFormChange('imageUrl', e.target.value)}
                      className="spotlight-input"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="spotlight-actions">
                  <button className="spotlight-cancel-button" onClick={handleCloseSpotlightModal}>Cancel</button>
                  <button
                    className="spotlight-confirm-button"
                    onClick={handleSpotlightSubmit}
                    disabled={spotlightLoading || !spotlightForm.author.trim() || !spotlightForm.imageUrl.trim()}
                  >
                    {spotlightLoading ? 'Assigning...' : '★ Assign Spotlight'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
